import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChefHat, Globe2, ChevronDown, Sprout, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { ZoomableImage } from './ZoomableImage';

const PLANNING = [
  {
    mois: 'Plats du Jour',
    semaines: [
      {
        titre: 'Semaine 1',
        jours: [
          { jour: 'Lundi', france: 'Lasagne', monde: 'Poulet Curry Coco avec Riz aux Légumes', veggie: 'Riz aux Légumes Ratatouille', img_fr: '/generated/pdj_lundi_lasagne.png', img_wd: '/generated/pdj_lundi_poulet_curry_coco.png', img_veg: '/generated/pdj_lundi_riz_ratatouille.png', dishId_fr: 'pdj_lundi_france', dishId_wd: 'pdj_lundi_monde', dishId_veg: 'pdj_lundi_veggie' },
          { jour: 'Mardi', france: 'Moussaka', monde: 'Poulet Caramélisé Nouilles Sautées', veggie: 'Tajine Légumes au Citron Confit', img_fr: '/generated/pdj_mardi_moussaka.png', img_wd: '/generated/pdj_mardi_poulet_caramelise.png', img_veg: '/generated/m1s1_j_veg_tajine_citron_confit_872d44a1.png', dishId_fr: 'pdj_mardi_france', dishId_wd: 'pdj_mardi_monde', dishId_veg: 'pdj_mardi_veggie' },
          { jour: 'Mercredi', france: 'Brandade de Morue', monde: 'Boulettes d\'Agneau, Sauce Barbecue, Riz Légumes', veggie: 'Nems Végétariens', img_fr: '/generated/pdj_mercredi_brandade.png', img_wd: '/generated/pdj_mercredi_boulettes_agneau.png', img_veg: '/generated/pdj_mercredi_nems_vege.png', dishId_fr: 'pdj_mercredi_france', dishId_wd: 'pdj_mercredi_monde', dishId_veg: 'pdj_mercredi_veggie' },
          { jour: 'Jeudi', france: 'Quiche Lorraine', monde: 'Rôti de Porc façon Asiatique, Riz Safrané', veggie: 'Salade Grecque Halloumi', img_fr: '/generated/pdj_jeudi_quiche_lorraine.png', img_wd: '/generated/pdj_jeudi_roti_porc_asiatique.png', img_veg: '/generated/pdj_jeudi_salade_grecque_halloumi.png', dishId_fr: 'pdj_jeudi_france', dishId_wd: 'pdj_jeudi_monde', dishId_veg: 'pdj_jeudi_veggie' },
          { jour: 'Vendredi', france: 'Filet de Lieu Noir en Papillote', monde: 'Crevettes Aigre-Douce, Riz Cantonais', veggie: 'Pad Thaï de Tofu Croustillant', img_fr: '/generated/pdj_vendredi_lieu_noir_papillote.png', img_wd: '/generated/pdj_vendredi_crevettes_aigre_douce.png', img_veg: '/generated/pdj_vendredi_pad_thai_tofu.png', dishId_fr: 'pdj_vendredi_france', dishId_wd: 'pdj_vendredi_monde', dishId_veg: 'pdj_vendredi_veggie' },
        ],
      },
    ],
  },
  {
    mois: 'Entrées',
    entrees: [
      {
        nom: 'Tartine de Chèvre Chaud au Miel d\'Acacia',
        description: 'Pain de campagne toasté, chèvre fondant, miel d\'acacia, noix concassées, roquette & tomates cerises, filet de balsamique.',
        img: '/generated/entree_tartine_chevre_miel_1dd0aca3.png',
        dishId: 'pdj_entree_tartine_chevre', price: 8,
      },
      {
        nom: 'Taboulé à l\'Oriental',
        description: 'Semoule fine, menthe fraîche, persil, tomate, concombre, oignon rouge, citron & huile d\'olive.',
        img: '/generated/entree_taboule_oriental_70153f09.png',
        dishId: 'pdj_entree_taboule_oriental', price: 8,
      },
      {
        nom: 'Taboulé à l\'Oriental & Dés de Poulet',
        description: 'Notre taboulé oriental enrichi de dés de blanc de poulet mariné et grillé aux herbes.',
        img: '/generated/entree_taboule_poulet_c1b0f57f.png',
        dishId: 'pdj_entree_taboule_poulet', price: 8,
      },
      {
        nom: 'Caviar d\'Aubergine',
        description: 'Caviar d\'aubergine fumé à l\'huile d\'olive, herbes fraîches, pain toasté.',
        img: '/generated/entree_caviar_aubergine.png',
        dishId: 'pdj_entree_caviar_aubergine', price: 8,
      },
      {
        nom: 'Rillettes de Thon',
        description: 'Rillettes de thon maison à la ciboulette et zeste de citron, baguette toastée.',
        img: '/generated/entree_rillettes_thon.png',
        dishId: 'pdj_entree_rillettes_thon', price: 8,
      },
      {
        nom: 'Tomates Mozzarella Échalotes',
        description: 'Tomates mûres, mozzarella fraîche, échalotes ciselées, basilic et balsamique.',
        img: '/generated/entree_tomates_mozza_echalotes.png',
        dishId: 'pdj_entree_tomates_mozza', price: 8,
      },
      {
        nom: 'Salade de Lentilles Cervelas Échalotes',
        description: 'Lentilles vertes, rondelles de cervelas, échalotes, vinaigrette moutardée.',
        img: '/generated/entree_lentilles_cervelas.png',
        dishId: 'pdj_entree_lentilles_cervelas', price: 8,
      },
      {
        nom: 'Salade Composée',
        description: 'Mesclun de jeunes pousses, jambon cru, œuf poché coulant, croûtons dorés, tomates cerises, copeaux de parmesan, vinaigrette aux herbes.',
        img: '/generated/entree_salade_composee_09ee74bc.png',
        dishId: 'pdj_entree_salade_composee', price: 8,
      },
      {
        nom: 'Salade de Pommes de Terre au Thon & Légumes du Soleil',
        description: 'Grenailles vapeur, thon albacore, poivrons rôtis, olives de Nyons, câpres, oignon rouge, vinaigrette citronnée.',
        img: '/generated/entree_salade_pdt_thon_b6ed3f83.png',
        dishId: 'pdj_entree_salade_pdt_thon', price: 8,
      },
      {
        nom: 'Œuf Mimosa au Thon & Dés de Saumon',
        description: 'Œufs durs garnis d\'un émulsion thon-mayonnaise maison, dés de saumon fumé, aneth frais, câpres, sur mesclun.',
        img: '/generated/entree_oeuf_mimosa_thon_saumon_44afc580.png',
        dishId: 'pdj_entree_oeuf_mimosa_thon_saumon', price: 8,
      },
      {
        nom: 'Carpaccio de Melon à la Gelée de Porto',
        description: 'Fines tranches de melon bien mûr disposées en rosace, gelée de Porto rubis, menthe fraîche, poivre du moulin.',
        img: '/generated/entree_carpaccio_melon_porto_6490f53e.png',
        dishId: 'pdj_entree_carpaccio_melon_porto', price: 8,
      },
    ],
  },
  {
    mois: 'Desserts',
    entrees: [
      {
        nom: 'Soupe de Pêche à la Menthe & Gingembre',
        description: 'Coulis de pêches jaunes gorgées de soleil, tranches fines, menthe fraîche, éclats de gingembre confit, quenelle de glace vanille.',
        img: '/generated/dessert_soupe_peche_menthe_gingembre_2d031dcc.png',
        dishId: 'pdj_dessert_soupe_peche', price: 6,
      },
      {
        nom: 'Clafoutis Poire',
        description: 'Clafoutis moelleux aux poires pochées, appareil vanillé, filet de caramel, sucre glace, crème fraîche battue.',
        img: '/generated/dessert_clafoutis_poire_31a700a2.png',
        dishId: 'pdj_dessert_clafoutis_poire', price: 6,
      },
      {
        nom: 'Moelleux au Chocolat Fondant Cœur Passion',
        description: 'Petit gâteau chocolat noir tiède, cœur coulant au fruit de la passion, quenelle glace vanille, éclats de cacao.',
        img: '/generated/dessert_moelleux_chocolat_passion_967a133a.png',
        dishId: 'pdj_dessert_moelleux_chocolat_passion', price: 6,
      },
      {
        nom: 'Crème Brûlée Pistache',
        description: 'Crème onctueuse à la pistache d\'Iran, croûte de sucre caramélisé au chalumeau, éclats de pistaches torréfiées.',
        img: '/generated/dessert_creme_brulee_pistache_82676146.png',
        dishId: 'pdj_dessert_creme_brulee_pistache', price: 6,
      },
      {
        nom: 'Poire Pochée & Éclats de Chocolat Noir',
        description: 'Poire pochée au sirop épicé, glaçage caramel, copeaux et éclats de chocolat noir grand cru, quenelle glace vanille.',
        img: '/generated/dessert_poire_pochee_chocolat_bec214c9.png',
        dishId: 'pdj_dessert_poire_pochee_chocolat', price: 6,
      },
      {
        nom: 'Carpaccio Ananas, Amandes Effilées & Malibu',
        description: 'Fines tranches d\'ananas Victoria en rosace, sirop Malibu-coco, amandes effilées torréfiées, menthe fraîche, sorbet coco.',
        img: '/generated/dessert_carpaccio_ananas_malibu_b240e628.png',
        dishId: 'pdj_dessert_carpaccio_ananas_malibu', price: 6,
      },
    ],
  },
];

const TARIFS = [
  { label: 'Entrée', price: '8.–' },
  { label: 'Entrée Principale', price: '16.–' },
  { label: 'Dessert', price: '6.–' },
  { label: 'Plat', price: '18.–' },
  { label: 'Entrée + Plat', price: '22.–' },
  { label: 'Plat + Dessert', price: '22.–' },
  { label: 'Menu Complet', price: '26.–' },
];

export const PlanningSemaine = () => {
  const [openMois, setOpenMois] = useState(0);
  const { addToCart } = useCart();

  const handleAddToCart = async (dishId, name) => {
    if (!dishId) return;
    try {
      await addToCart(dishId, 1);
      toast.success(`${name} ajouté au panier`);
    } catch (error) {
      toast.error('Impossible d\'ajouter au panier');
    }
  };

  const AddBtn = ({ dishId, name }) =>
    dishId ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleAddToCart(dishId, name); }}
        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-terracotta text-white text-[11px] font-medium hover:bg-terracotta/90 transition-colors"
        data-testid={`addtocart-${dishId}`}
        title="Ajouter au panier"
      >
        <Plus size={12} strokeWidth={3} />
        Panier
      </button>
    ) : null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto mb-16"
      data-testid="planning-semaine"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-navy to-[#2a3142] rounded-2xl px-8 py-8 text-center mb-6 shadow-card">
        <div className="flex items-center justify-center gap-3 mb-2">
          <span className="w-12 h-[1px] bg-terracotta"></span>
          <Calendar className="text-terracotta" size={22} />
          <span className="w-12 h-[1px] bg-terracotta"></span>
        </div>
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-wide">
          Planning Menu Semaine
        </h2>
        <p className="text-white/85 mt-2 italic font-heading">
          Triple Menu Quotidien : Terroir Français, Saveurs du Monde & Végétarien
        </p>
      </div>

      {/* Mois accordéons */}
      <div className="space-y-4 mb-6">
        {PLANNING.map((m, idx) => (
          <div key={m.mois} className="bg-[#FAF8F4] rounded-2xl border border-[#D4C4AA]/40 shadow-card overflow-hidden">
            <button
              onClick={() => setOpenMois(openMois === idx ? -1 : idx)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-terracotta/5 transition-colors"
              data-testid={`planning-mois-${idx}`}
            >
              <h3 className="font-heading text-xl md:text-2xl font-bold text-navy text-left">{m.mois}</h3>
              <ChevronDown size={24} className={`text-terracotta transition-transform ${openMois === idx ? 'rotate-180' : ''}`} />
            </button>

            {openMois === idx && (
              <div className="px-6 pb-6 space-y-6">
                {m.entrees ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid={`planning-entrees-${idx}`}>
                    {m.entrees.map((e) => (
                      <div key={e.nom} className="flex gap-4 bg-white/70 rounded-xl p-4 border border-[#D4C4AA]/40 hover:border-terracotta/60 transition-colors">
                        {e.img && (
                          <ZoomableImage
                            src={e.img}
                            alt={e.nom}
                            loading="lazy"
                            className="w-24 h-24 md:w-28 md:h-28 rounded-lg object-cover border border-[#D4C4AA]/60 shadow-sm flex-shrink-0"
                            data-testid={`planning-entree-img-${e.nom}`}
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h5 className="font-heading font-bold text-navy text-base leading-snug">{e.nom}</h5>
                            {typeof e.price === 'number' && (
                              <span className="font-heading font-bold text-terracotta text-base whitespace-nowrap">{e.price}.–</span>
                            )}
                          </div>
                          {m.mois !== 'Entrées' && (
                            <p className="text-navy/75 text-sm italic leading-relaxed mb-2">{e.description}</p>
                          )}
                          <AddBtn dishId={e.dishId} name={e.nom} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                m.semaines.map((s) => {
                  const hasVeggie = s.jours.some((j) => j.veggie);
                  return (
                  <div key={s.titre}>
                    <h4 className="font-heading text-base font-bold text-terracotta tracking-widest uppercase mb-3 text-center">
                      {s.titre}
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-[#D4C4AA]/30">
                            <th className="text-left px-4 py-3 font-bold text-navy w-28">Jour</th>
                            <th className="text-left px-4 py-3 font-bold text-navy">
                              <div className="flex items-center gap-2">
                                <ChefHat size={16} className="text-terracotta" />
                                Côté France (Classique)
                              </div>
                            </th>
                            <th className="text-left px-4 py-3 font-bold text-navy">
                              <div className="flex items-center gap-2">
                                <Globe2 size={16} className="text-sage" />
                                Côté Monde (Exotique)
                              </div>
                            </th>
                            {hasVeggie && (
                              <th className="text-left px-4 py-3 font-bold text-navy">
                                <div className="flex items-center gap-2">
                                  <Sprout size={16} className="text-green-700" />
                                  Côté Végétarien
                                </div>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {s.jours.map((j) => (
                            <tr key={j.jour} className="border-t border-[#D4C4AA]/30 hover:bg-terracotta/5 transition-colors">
                              <td className="px-4 py-3 font-bold text-navy align-middle">{j.jour}</td>
                              <td className="px-4 py-3 text-navy/85 italic align-middle">
                                <div className="flex items-center gap-3">
                                  {j.img_fr && (
                                    <ZoomableImage
                                      src={j.img_fr}
                                      alt={j.france}
                                      loading="lazy"
                                      className="w-12 h-12 rounded-lg object-cover border border-[#D4C4AA]/60 shadow-sm flex-shrink-0"
                                      data-testid={`planning-vignette-fr-${j.jour}`}
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <span className="block mb-1.5">{j.france}</span>
                                    <AddBtn dishId={j.dishId_fr} name={j.france} />
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-navy/85 italic align-middle">
                                <div className="flex items-center gap-3">
                                  {j.img_wd && (
                                    <ZoomableImage
                                      src={j.img_wd}
                                      alt={j.monde}
                                      loading="lazy"
                                      className="w-12 h-12 rounded-lg object-cover border border-[#D4C4AA]/60 shadow-sm flex-shrink-0"
                                      data-testid={`planning-vignette-wd-${j.jour}`}
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <span className="block mb-1.5">{j.monde}</span>
                                    <AddBtn dishId={j.dishId_wd} name={j.monde} />
                                  </div>
                                </div>
                              </td>
                              {hasVeggie && (
                                <td className="px-4 py-3 text-navy/85 italic align-middle">
                                  {j.veggie ? (
                                    <div className="flex items-center gap-3">
                                      {j.img_veg && (
                                        <ZoomableImage
                                          src={j.img_veg}
                                          alt={j.veggie}
                                          loading="lazy"
                                          className="w-12 h-12 rounded-lg object-cover border border-[#D4C4AA]/60 shadow-sm flex-shrink-0"
                                          data-testid={`planning-vignette-veg-${j.jour}`}
                                        />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <span className="block mb-1.5">{j.veggie}</span>
                                        <AddBtn dishId={j.dishId_veg} name={j.veggie} />
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-navy/40">—</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  );
                })
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Tarifs */}
      <div className="bg-white rounded-2xl border-2 border-terracotta/30 shadow-card p-6 md:p-8" data-testid="planning-tarifs">
        <h3 className="font-heading text-xl md:text-2xl font-bold text-navy text-center mb-1">
          Tarifs <span className="text-terracotta">(CHF)</span>
        </h3>
        <div className="w-20 h-[2px] bg-terracotta mx-auto mb-6"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {TARIFS.map((t) => (
            <div
              key={t.label}
              className="text-center bg-[#FAF8F4] rounded-xl py-5 px-3 border border-[#D4C4AA]/40 hover:border-terracotta/60 transition-colors"
            >
              <p className="text-navy/70 text-sm font-medium mb-1">{t.label}</p>
              <p className="font-heading text-3xl font-bold text-terracotta">{t.price}</p>
            </div>
          ))}
        </div>
        <p className="text-center text-navy/60 text-xs italic mt-5">
          Commande avant 10h pour le jour même ou 24h à l&apos;avance • Livraison offerte sur Genève dès 80 CHF
        </p>
      </div>
    </motion.section>
  );
};
